import {
  Flex,
  TooltipTrigger,
  Icon,
  TooltipContent,
  Tooltip,
} from "@repo/ping-design";
import { useCallback, useMemo } from "react";
import {
  MISSING_VALUE,
  BuildingPanelField,
} from "@repo/ping-react-js";
import { formatSectionTooltip } from "../../../../utils/formatters";
import cx from "classnames";
import "./CopeSection.scss";
import "./AoASection.scss";

export const CopeSection = ({
  section,
  copeSectionsAmt,
  isPLI,
  shouldBlurValues,
}: {
  section: {
    title: string;
    type: "MAPS" | "PLI";
    values: any[];
  };
  copeSectionsAmt: number;
  isPLI: boolean;
  shouldBlurValues?: boolean;
}) => {
  const renderField = useCallback(
    (field: any, index: number, isTopRow: boolean = false) => {
      if (section.type === "MAPS") {
        return (
          <Flex
            key={`${field.k}-${index}`}
            direction="row"
            gap="3"
            align="center"
            className={`CopeSection__Data__Maps ${isTopRow ? "top-row" : "grid-item"}`}
          >
            {field?.t ? (
              <Tooltip>
                <TooltipTrigger>
                  <span className="CopeSection__Data__Maps__Label">
                    {field.k}
                    <Icon iconName="Info" size={14} />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{field.t}</TooltipContent>
              </Tooltip>
            ) : (
              <span className="CopeSection__Data__Maps__Label">{field.k}</span>
            )}
            {field?.vt && !shouldBlurValues ? (
              <Tooltip>
                <TooltipTrigger>
                  <span
                    aria-label={shouldBlurValues ? "Available on a paid plan" : undefined}
                    className={cx("CopeSection__Data__Maps__Value", {
                      "CopeSection__Data__Maps__Value--Blurred":
                        shouldBlurValues,
                    })}
                  >
                    {shouldBlurValues ? "123456" : field.v || MISSING_VALUE}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{field.vt}</TooltipContent>
              </Tooltip>
            ) : (
              <span
                aria-label={shouldBlurValues ? "Available on a paid plan" : undefined}
                className={cx("CopeSection__Data__Maps__Value", {
                  "CopeSection__Data__Maps__Value--Blurred": shouldBlurValues,
                })}
              >
                {shouldBlurValues ? "123456" : field.v || MISSING_VALUE}
              </span>
            )}
          </Flex>
        );
      } else if (section.type === "PLI") {
        return (
          <Flex
            key={`${field.k}-${index}`}
            direction="row"
            gap="3"
            align="center"
            className={`CopeSection__Data__Maps ${isTopRow ? "top-row" : "grid-item"}`}
          >
            {field?.t ? (
              <Tooltip>
                <TooltipTrigger>
                  <span className="CopeSection__Data__Maps__Label">
                    {field.k}
                    <Icon iconName="Info" size={14} />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{field.t}</TooltipContent>
              </Tooltip>
            ) : (
              <span className="CopeSection__Data__Maps__Label">{field.k}</span>
            )}
            {field?.vt && !shouldBlurValues ? (
              <Tooltip>
                <TooltipTrigger>
                  <span
                    aria-label={shouldBlurValues ? "Available on a paid plan" : undefined}
                    className={cx("CopeSection__Data__Maps__Value", {
                      "CopeSection__Data__Maps__Value--Blurred":
                        shouldBlurValues,
                    })}
                  >
                    {shouldBlurValues ? "123456" : field.v || MISSING_VALUE}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{field.vt}</TooltipContent>
              </Tooltip>
            ) : (
              <span
                aria-label={shouldBlurValues ? "Available on a paid plan" : undefined}
                className={cx("CopeSection__Data__Maps__Value", {
                  "CopeSection__Data__Maps__Value--Blurred": shouldBlurValues,
                })}
              >
                {shouldBlurValues ? "123456" : field.v || MISSING_VALUE}
              </span>
            )}
          </Flex>
        );
      }
    },
    [section.type, shouldBlurValues],
  );

  const sectionNumber = useMemo(() => {
    if (isPLI) {
      return 4;
    } else {
      return copeSectionsAmt + 2;
    }
  }, [isPLI, copeSectionsAmt]);

  const topRowValues: BuildingPanelField[] = useMemo(
    () =>
      section?.values?.slice(0, isPLI ? sectionNumber : sectionNumber) || [],
    [section, isPLI],
  );
  const gridValues: BuildingPanelField[] = useMemo(
    () => section?.values?.slice(isPLI ? sectionNumber : sectionNumber) || [],
    [section, isPLI],
  );

  return (
    <div className="CopeSection">
      {
        <div className="CopeSection__Data">
          {topRowValues.length > 0 && (
            <div className="CopeSection__Data__TopRow">
              {topRowValues.map((field, index) =>
                renderField(formatSectionTooltip(field, true), index, true),
              )}
            </div>
          )}
          <hr />
          {gridValues.length > 0 && (
            <div className="CopeSection__Data__GridContainer">
              {gridValues.map((field, index) =>
                renderField(formatSectionTooltip(field), index),
              )}
            </div>
          )}
        </div>
      }
    </div>
  );
};
